import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Plus,
  ArrowLeft,
  Loader2,
  Shield,
  ShieldCheck,
  User,
  Trash2,
  Edit3,
  X,
  Check,
  Sparkles,
  Search,
  RotateCcw,
  KeyRound,
} from "lucide-react";
import { apiv2, type UserOut } from "../apiv2";
import { rbacAdmin, type RoleOut } from "../apiv1";
import { useAuth } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onBack: () => void;
}

const DEFAULT_ROLE_META = {
  label: "Unknown role",
  color: "text-white/60 bg-white/5 border-white/10",
  icon: User,
};

// Friendly display for known role names. Falls back to DEFAULT_ROLE_META
// (or the raw role name) for any role not listed here instead of crashing
// the page — see the PR fixing the blank-screen bug when a role name had
// no matching entry.
const ROLE_LABELS: Record<
  string,
  { label: string; color: string; icon: typeof Shield }
> = {
  admin: {
    label: "Admin",
    color: "text-red-400 bg-red-500/10 border-red-500/20",
    icon: ShieldCheck,
  },
  platform_admin: {
    label: "Platform Admin",
    color: "text-red-400 bg-red-500/10 border-red-500/20",
    icon: ShieldCheck,
  },
  project_user: {
    label: "Project User",
    color: "text-brand-400 bg-brand-500/10 border-brand-500/20",
    icon: Shield,
  },
  general_user: {
    label: "Reviewer",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    icon: User,
  },
  conference_attendee: {
    label: "Demo User",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    icon: User,
  },
  demo_converter_guardian_profiler: {
    label: "Demo User",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    icon: User,
  },
  data_engineer: {
    label: "Data Engineer",
    color: "text-brand-400 bg-brand-500/10 border-brand-500/20",
    icon: Shield,
  },
  data_steward: {
    label: "Data Steward",
    color: "text-brand-400 bg-brand-500/10 border-brand-500/20",
    icon: Shield,
  },
  engagement_manager: {
    label: "Engagement Manager",
    color: "text-brand-400 bg-brand-500/10 border-brand-500/20",
    icon: Shield,
  },
  hitl_reviewer: {
    label: "HITL Reviewer",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    icon: User,
  },
  security_auditor: {
    label: "Security Auditor",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    icon: User,
  },
};

function roleLabel(roleName: string): string {
  return ROLE_LABELS[roleName]?.label ?? roleName;
}

export default function UserManagement({ onBack }: Props) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState<UserOut[]>([]);
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    password: "",
    role_id: "",
  });
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRoleId, setEditRoleId] = useState<string>("");

  const load = useCallback(async () => {
    try {
      const [userList, roleList] = await Promise.all([
        apiv2.listUsers(),
        rbacAdmin.listRoles(),
      ]);
      setUsers(userList);
      setRoles(roleList);
      setForm((f) => ({ ...f, role_id: f.role_id || roleList[0]?.id || "" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleIdByName = (name: string) => roles.find((r) => r.name === name)?.id;

  const handleCreate = async () => {
    if (
      !form.email.trim() ||
      !form.full_name.trim() ||
      !form.password.trim() ||
      !form.role_id
    )
      return;
    setCreating(true);
    setError(null);
    try {
      await apiv2.createUser({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        password: form.password,
        role_id: form.role_id,
      });
      setForm({
        email: "",
        full_name: "",
        password: "",
        role_id: roles[0]?.id ?? "",
      });
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateRole = async (userId: string, roleId: string) => {
    if (!roleId) return;
    try {
      await rbacAdmin.assignUserRole(userId, roleId);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  const handleDeactivate = async (userId: string) => {
    if (
      !confirm("Deactivate this user? They will no longer be able to sign in.")
    )
      return;
    try {
      await apiv2.setUserActive(userId, false);
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to deactivate user",
      );
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      await apiv2.setUserActive(userId, true);
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reactivate user",
      );
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] relative">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <Loader2
          size={24}
          className="animate-spin text-brand-500 relative z-10"
        />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto custom-scrollbar h-full relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <GreenTendrils />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in">
          <div className="flex items-center gap-5">
            <button
              onClick={onBack}
              className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all duration-300 hover:bg-brand-500/5"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <ArrowLeft size={18} className="text-white/50" />
            </button>
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-3"
                style={{ background: "rgba(134,188,37,0.08)" }}
              >
                <Sparkles size={12} />
                Administration
              </div>
              <h1 className="text-4xl font-black tracking-tight">
                <span className="gradient-text-green">Users & Roles</span>
              </h1>
              <p className="text-sm text-white/40 mt-1">
                {users.length} users &middot;{" "}
                {users.filter((u) => u.is_active).length} active
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-5 py-2.5 text-white rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
              boxShadow:
                "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
            }}
          >
            <Plus size={16} /> Add User
          </button>
        </div>

        {/* Module tabs — Users / Roles & Permissions are one admin area */}
        <div className="flex items-center gap-2 border-b border-white/8">
          <button
            className="px-4 py-2 text-sm font-semibold text-brand-400 border-b-2 border-brand-500"
            disabled
          >
            Users
          </button>
          <button
            onClick={() => navigate("/admin/roles")}
            className="px-4 py-2 text-sm font-semibold text-white/40 hover:text-white/70 flex items-center gap-1.5"
          >
            <KeyRound size={14} /> Roles & Permissions
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs ml-4"
            >
              dismiss
            </button>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div
            className="glass rounded-2xl p-6 space-y-4 animate-fade-in"
            style={{
              background: "rgba(28,28,42,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Plus size={14} className="text-brand-500" /> New User
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="user@company.com"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                  placeholder="Jane Doe"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  placeholder="Min 8 characters"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">Role</label>
                <select
                  value={form.role_id}
                  onChange={(e) =>
                    setForm({ ...form, role_id: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50"
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {roleLabel(r.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreate}
                disabled={
                  creating ||
                  !form.email ||
                  !form.full_name ||
                  !form.password ||
                  !form.role_id
                }
                className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 flex items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                }}
              >
                {creating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Create
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-lg text-xs text-white/50 hover:text-white/80 border border-white/8 hover:border-white/20 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-10 pr-4 py-2.5 bg-surface-300/30 border border-white/8 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
          />
        </div>

        {/* User list */}
        <div className="space-y-2">
          {filtered.map((u) => {
            const roleMeta = ROLE_LABELS[u.role] ?? DEFAULT_ROLE_META;
            const RoleIcon = roleMeta.icon;
            const isMe = u.id === me?.id;

            return (
              <div
                key={u.id}
                className={`glass rounded-xl p-4 flex items-center gap-4 transition-all ${!u.is_active ? "opacity-50" : ""}`}
                style={{
                  background: "rgba(28,28,42,0.4)",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                  <RoleIcon size={18} className="text-brand-500" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white truncate">
                      {u.full_name}
                    </span>
                    {isMe && (
                      <span className="text-[11px] text-brand-400 bg-brand-500/10 px-1.5 py-0.5 rounded">
                        you
                      </span>
                    )}
                    {!u.is_active && (
                      <span className="text-[11px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                        inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 truncate">{u.email}</p>
                </div>

                {/* Role badge */}
                {editingId === u.id ? (
                  <div className="flex items-center gap-2">
                    <select
                      value={editRoleId}
                      onChange={(e) => setEditRoleId(e.target.value)}
                      className="px-2 py-1 bg-surface-300/50 border border-white/8 rounded-lg text-xs text-white focus:outline-none"
                    >
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {roleLabel(r.name)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleUpdateRole(u.id, editRoleId)}
                      className="p-1 text-brand-500 hover:text-brand-400"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1 text-white/30 hover:text-white/60"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <span
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${roleMeta.color}`}
                  >
                    {roleMeta.label}
                  </span>
                )}

                {/* Actions */}
                {!isMe && u.is_active && editingId !== u.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingId(u.id);
                        setEditRoleId(
                          roleIdByName(u.role) ?? roles[0]?.id ?? "",
                        );
                      }}
                      className="p-1.5 rounded-lg text-white/20 hover:text-white/60 hover:bg-white/5 transition"
                      title="Change role"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeactivate(u.id)}
                      className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition"
                      title="Deactivate"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
                {!isMe && !u.is_active && (
                  <button
                    onClick={() => handleReactivate(u.id)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-green-400 bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition"
                    title="Reactivate user"
                  >
                    <RotateCcw size={12} /> Reactivate
                  </button>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-white/30 text-sm">
              <Users size={32} className="mx-auto mb-3 opacity-50" />
              {search ? "No users match your search" : "No users yet"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
